import { UntypedFormBuilder, UntypedFormGroup, UntypedFormArray, Validators } from '@angular/forms';

export class LocationSettingsFormFactory {
  static buildForm(fb: UntypedFormBuilder): UntypedFormGroup {
    return fb.group({
      general: fb.group({
        merchantLocationId: [''],
        name: ['', [Validators.required, Validators.maxLength(200)]],
        slug: ['', [Validators.required, Validators.maxLength(200), Validators.pattern(/^[a-z0-9-]+$/)]],
        description: ['', Validators.maxLength(1000)],
        statusMerchantLocationId: ['', Validators.required],
        isMainLocation: [false],
        displayOrder: [0, [Validators.min(0)]],
        phone: ['', Validators.maxLength(20)],
        email: ['', [Validators.email, Validators.maxLength(100)]],
        addressLine1: ['', [Validators.required, Validators.maxLength(200)]],
        addressLine2: ['', Validators.maxLength(200)],
        city: ['', [Validators.required, Validators.maxLength(100)]],
        state: ['', [Validators.required, Validators.maxLength(100)]],
        postalCode: ['', [Validators.required, Validators.maxLength(20)]],
        countryCode: ['', [Validators.required, Validators.maxLength(3)]],
        latitude: [''],
        longitude: [''],
        timeZoneId: ['', [Validators.required, Validators.maxLength(100)]],
        currencyCode: ['', [Validators.required, Validators.maxLength(3)]],
        businessHours: ['', Validators.maxLength(1000)],
        merchantLocationCategoryId: ['', Validators.required]
      }),

      media: fb.group({
        logoUrl: [''],
        bannerUrl: [''],
        primaryColor: ['#1976d2'],
        secondaryColor: ['#424242'],
        accentColor: ['#ff4081'],
        images: fb.array([]),
        sliderImages: fb.array([])
      }),

      services: fb.group({
        concepts: fb.array([])
      }),

      staff: fb.group({
        members: fb.array([])
      }),

      staffConcepts: fb.array([]),

      availability: fb.group({
        weeklyRules: fb.array([]),
        defaultSlotDuration: [30, [Validators.required, Validators.min(5)]],
        slotStep: [30, [Validators.required, Validators.min(5)]],
        minAdvanceBooking: [60, Validators.min(0)],
        maxAdvanceBooking: [90, Validators.min(0)],
      }),

      blocks: fb.group({
        exceptions: fb.array([]),
        manualBlocks: fb.array([]),
        vacations: fb.array([])
      }),

      bookingSettings: fb.group({
        allowOnlineBooking: [true],
        requireApproval: [false],
        requireDeposit: [false],
        depositAmount: [0, Validators.min(0)],
        depositType: ['Fixed'],
        sendConfirmationEmail: [true],
        sendReminderEmail: [true],
        reminderHoursBefore: [24, Validators.min(0)],
        allowCancellation: [true],
        cancellationHoursBefore: [24, Validators.min(0)],
        maxConcurrentBookings: [1, [Validators.required, Validators.min(1)]],
        bufferBefore: [0, Validators.min(0)],
        bufferAfter: [0, Validators.min(0)],
        requireCustomerPhone: [true],
        requireCustomerNotes: [false],
        customFields: fb.array([])
      })
    });
  }

  static createImageFormGroup(fb: UntypedFormBuilder): UntypedFormGroup {
    return fb.group({
      id: [''],
      url: ['', Validators.required],
      title: ['', Validators.maxLength(200)],
      description: ['', Validators.maxLength(500)],
      displayOrder: [0, Validators.min(0)],
      isActive: [true]
    });
  }

  static createSliderImageFormGroup(fb: UntypedFormBuilder): UntypedFormGroup {
    return fb.group({
      id: [''],
      url: ['', Validators.required],
      title: ['', Validators.maxLength(200)],
      subtitle: ['', Validators.maxLength(500)],
      linkUrl: [''],
      displayOrder: [0, Validators.min(0)],
      isActive: [true]
    });
  }

  static createConceptFormGroup(fb: UntypedFormBuilder): UntypedFormGroup {
    return fb.group({
      id: [''],
      name: ['', [Validators.required, Validators.maxLength(200)]],
      description: ['', Validators.maxLength(1000)],
      duration: [30, [Validators.required, Validators.min(5)]],
      price: [0, [Validators.required, Validators.min(0)]],
      bufferBefore: [0, Validators.min(0)],
      bufferAfter: [0, Validators.min(0)],
      status: ['Active', Validators.required],
      displayOrder: [0, Validators.min(0)],
      isActive: [true],
      color: ['#1976d2'],
      maxConcurrent: [1, Validators.min(1)],
      requireDeposit: [false],
      depositAmount: [0, Validators.min(0)]
    });
  }

  static createStaffConceptFormGroup(fb: UntypedFormBuilder): UntypedFormGroup {
    return fb.group({
      id: [''],
      staffId: ['', Validators.required],
      staffName: [''],
      conceptId: ['', Validators.required],
      conceptName: [''],
      isActive: [true],
      displayOrder: [0, Validators.min(0)]
    });
  }

  static createWeeklyRuleFormGroup(fb: UntypedFormBuilder): UntypedFormGroup {
    return fb.group({
      id: [''],
      dayOfWeek: [1, [Validators.required, Validators.min(0), Validators.max(6)]],
      startTime: ['09:00', Validators.required],
      endTime: ['18:00', Validators.required],
      slotDuration: [30, [Validators.min(5)]],
      isActive: [true]
    });
  }

  static createExceptionFormGroup(fb: UntypedFormBuilder): UntypedFormGroup {
    return fb.group({
      id: [''],
      date: ['', Validators.required],
      startTime: [''],
      endTime: [''],
      reason: ['', Validators.maxLength(500)],
      type: ['Closed'],
      isActive: [true],
      isClosed: [true]
    });
  }

  static createManualBlockFormGroup(fb: UntypedFormBuilder): UntypedFormGroup {
    return fb.group({
      id: [''],
      startDateTime: ['', Validators.required],
      endDateTime: ['', Validators.required],
      reason: ['', [Validators.required, Validators.maxLength(500)]],
      type: ['Manual'],
      affectsAllStaff: [false],
      staffIds: [[]],
      isActive: [true]
    });
  }

  static createVacationFormGroup(fb: UntypedFormBuilder): UntypedFormGroup {
    return fb.group({
      id: [''],
      staffId: ['', Validators.required],
      staffName: [''],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      reason: ['', Validators.maxLength(500)],
      type: ['Vacation'],
      isActive: [true]
    });
  }

  static createCustomFieldFormGroup(fb: UntypedFormBuilder): UntypedFormGroup {
    return fb.group({
      id: [''],
      name: ['', [Validators.required, Validators.maxLength(100)]],
      label: ['', [Validators.required, Validators.maxLength(200)]],
      type: ['Text', Validators.required],
      isRequired: [false],
      displayOrder: [0, Validators.min(0)],
      options: [''],
      placeholder: ['', Validators.maxLength(200)],
      helpText: ['', Validators.maxLength(500)]
    });
  }

  static createServiceConceptFormGroup(fb: UntypedFormBuilder, data?: any): UntypedFormGroup {
    return fb.group({
      conceptUid: [data ? data.conceptUid : '', [Validators.required]],
      bookingTitle: [data ? data.bookingTitle : '', [Validators.required, Validators.maxLength(200)]],
      bookingDescription: [data ? data.bookingDescription : '', [Validators.maxLength(1000)]],
      durationInMinutes: [data ? data.durationInMinutes : 30, [Validators.required, Validators.min(1)]],
      price: [data ? data.price : 0, [Validators.required, Validators.min(0)]],
      preBufferInMinutes: [data ? data.preBufferInMinutes : 0, [Validators.min(0)]],
      postBufferInMinutes: [data ? data.postBufferInMinutes : 0, [Validators.min(0)]],
      status: [data ? data.status : 1, [Validators.required]],
      staffUids: [data ? data.staffUids : []]
    });
  }

  static images(form: UntypedFormGroup): UntypedFormArray {
    return form.get('media.images') as UntypedFormArray;
  }

  static sliderImages(form: UntypedFormGroup): UntypedFormArray {
    return form.get('media.sliderImages') as UntypedFormArray;
  }

  static serviceConcepts(form: UntypedFormGroup): UntypedFormArray {
    return form.get('services.concepts') as UntypedFormArray;
  }

  static staffConcepts(form: UntypedFormGroup): UntypedFormArray {
    return form.get('staffConcepts') as UntypedFormArray;
  }

  static weeklyRules(form: UntypedFormGroup): UntypedFormArray {
    return form.get('availability.weeklyRules') as UntypedFormArray;
  }

  static exceptions(form: UntypedFormGroup): UntypedFormArray {
    return form.get('blocks.exceptions') as UntypedFormArray;
  }

  static manualBlocks(form: UntypedFormGroup): UntypedFormArray {
    return form.get('blocks.manualBlocks') as UntypedFormArray;
  }

  static vacations(form: UntypedFormGroup): UntypedFormArray {
    return form.get('blocks.vacations') as UntypedFormArray;
  }

  static customFields(form: UntypedFormGroup): UntypedFormArray {
    return form.get('bookingSettings.customFields') as UntypedFormArray;
  }
}
